import { API } from '@/lib/api';
import { useState, useEffect } from "react";
import { getErrorMessage } from "@/lib/utils";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Shield, Upload, Camera, FileText, CheckCircle2, AlertCircle, Loader2, ArrowLeft, ArrowRight, X, Truck, Wrench } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";



const SERVICE_OPTIONS = [
  { value: "cargo", label: "Cargo Delivery" },
  { value: "courier", label: "Courier" },
  { value: "towing", label: "Towing" },
  { value: "movers", label: "Movers" },
  { value: "roadside", label: "Roadside Assistance" },
  { value: "laborer", label: "General Labor" },
  { value: "nanny", label: "Nanny / Childcare" },
  { value: "cleaning", label: "Cleaning" },
  { value: "private_chef", label: "Private Chef" },
];

const DOC_LABELS = {
  government_id: { label: "Government-Issued ID", desc: "Passport, national ID, or provincial ID" },
  drivers_license: { label: "Driver's License", desc: "Valid driver's license" },
  vehicle_registration: { label: "Vehicle Registration", desc: "Current vehicle registration" },
  vehicle_insurance: { label: "Vehicle Insurance", desc: "Active insurance certificate" },
  certification: { label: "Professional Certification", desc: "Relevant certification or diploma" },
  background_check: { label: "Background Check", desc: "Police clearance or background check" },
  food_safety_certificate: { label: "Food Safety Certificate", desc: "Food handler's certification" },
};

const ProfessionalVerificationPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [selectedServices, setSelectedServices] = useState([]);
  const [requiredDocs, setRequiredDocs] = useState([]);
  const [uploadedDocs, setUploadedDocs] = useState({});
  const [selfieFile, setSelfieFile] = useState(null);
  const [selfiePreview, setSelfiePreview] = useState(null);
  const [experience, setExperience] = useState("");
  const [vehicleType, setVehicleType] = useState("");
  const [roadsideServices, setRoadsideServices] = useState([]);
  const [laborerJobTypes, setLaborerJobTypes] = useState([]);
  const [nannyExperience, setNannyExperience] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [existingApp, setExistingApp] = useState(null);
  const [uploading, setUploading] = useState({});

  useEffect(() => {
    checkExistingApplication();
  }, []);

  const checkExistingApplication = async () => {
    try {
      const res = await axios.get(`${API}/user/professional/status`, { withCredentials: true });
      if (res.data.application) {
        setExistingApp(res.data.application);
      }
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    if (selectedServices.length > 0) {
      fetchRequiredDocs();
    }
  }, [selectedServices]);

  const fetchRequiredDocs = async () => {
    try {
      const res = await axios.get(`${API}/user/professional/required-docs?services=${selectedServices.join(",")}`);
      setRequiredDocs(res.data.required_documents);
    } catch (err) {
      console.error(err);
    }
  };

  const toggleService = (value) => {
    setSelectedServices(prev =>
      prev.includes(value) ? prev.filter(s => s !== value) : [...prev, value]
    );
  };

  const handleFileUpload = async (docType, file) => {
    if (!file) return;
    setUploading(prev => ({ ...prev, [docType]: true }));
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("doc_type", docType);
      formData.append("applicant_type", "professional");
      const res = await axios.post(`${API}/upload/document`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setUploadedDocs(prev => ({ ...prev, [docType]: { url: res.data.url, name: file.name } }));
      toast.success(`${DOC_LABELS[docType]?.label || docType} uploaded`);
    } catch (err) {
      toast.error(getErrorMessage(err, "Upload failed. Please try again."));
    } finally {
      setUploading(prev => ({ ...prev, [docType]: false }));
    }
  };

  const handleSelfieUpload = async (file) => {
    if (!file) return;
    setSelfiePreview(URL.createObjectURL(file));
    setSelfieFile(file);
    setUploading(prev => ({ ...prev, selfie: true }));
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("doc_type", "selfie");
      formData.append("applicant_type", "professional");
      const res = await axios.post(`${API}/upload/document`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setSelfieFile(res.data.url);
      toast.success("Selfie uploaded");
    } catch (err) {
      toast.error(getErrorMessage(err, "Selfie upload failed"));
    } finally {
      setUploading(prev => ({ ...prev, selfie: false }));
    }
  };

  const handleSubmit = async () => {
    if (selectedServices.length === 0) return toast.error("Select at least one service");
    const missingDocs = requiredDocs.filter(d => !uploadedDocs[d]);
    if (missingDocs.length > 0) return toast.error(`Missing documents: ${missingDocs.map(d => DOC_LABELS[d]?.label || d).join(", ")}`);
    if (!selfieFile) return toast.error("Selfie verification is required");

    setSubmitting(true);
    try {
      const documents = Object.entries(uploadedDocs).map(([type, data]) => ({
        type,
        url: data.url,
        name: data.name,
      }));

      await axios.post(`${API}/user/professional/apply`, {
        services: selectedServices,
        documents,
        selfie_url: typeof selfieFile === "string" ? selfieFile : "",
        experience,
        vehicle_type: selectedServices.includes("cargo") ? vehicleType : undefined,
        roadside_services: selectedServices.includes("roadside") ? roadsideServices : undefined,
        laborer_job_types: selectedServices.includes("laborer") ? laborerJobTypes : undefined,
        nanny_experience: selectedServices.includes("nanny") ? nannyExperience : undefined,
      }, { withCredentials: true });

      toast.success("Application submitted! We'll review it shortly.");
      navigate("/profile");
    } catch (err) {
      toast.error(getErrorMessage(err, "Failed to submit application"));
    } finally {
      setSubmitting(false);
    }
  };

  const handleReapply = () => {
    setExistingApp(null);
  };

  // Already applied — show status
  if (existingApp) {
    const statusConfig = {
      pending: { color: "bg-yellow-100 text-yellow-800", icon: AlertCircle, message: "Your application is under review. We'll notify you once it's processed." },
      approved: { color: "bg-green-100 text-green-800", icon: CheckCircle2, message: "Your professional account is approved! Switch to Professional Mode from your profile." },
      rejected: { color: "bg-red-100 text-red-800", icon: AlertCircle, message: existingApp.admin_notes || "Your application was not approved. Please wait for admin to allow reapplication." },
      reapply_allowed: { color: "bg-blue-100 text-blue-800", icon: AlertCircle, message: "You have been allowed to reapply. Click below to submit a new application." },
    };
    const cfg = statusConfig[existingApp.status] || statusConfig.pending;
    const StatusIcon = cfg.icon;

    return (
      <div className="min-h-screen bg-stone-50" data-testid="verification-status-page">
        <Navbar />
        <section className="relative pt-32 pb-12">
          <div className="absolute inset-0 bg-gradient-to-r from-emerald-950 via-emerald-900 to-emerald-950"></div>
          <div className="section-container relative z-10">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
              <h1 className="text-3xl md:text-4xl font-bold text-white">Verification Status</h1>
              <p className="text-emerald-300 mt-2">Professional Account Application</p>
            </motion.div>
          </div>
        </section>
        <section className="py-12 px-4">
          <div className="max-w-lg mx-auto">
            <div className="bg-white rounded-2xl p-8 border border-stone-100 text-center" data-testid="verification-status-card">
              <StatusIcon className={`w-16 h-16 mx-auto mb-4 ${existingApp.status === "approved" ? "text-green-500" : existingApp.status === "rejected" ? "text-red-500" : "text-yellow-500"}`} />
              <Badge className={`${cfg.color} text-sm mb-4`} data-testid="verification-status-badge">{existingApp.status}</Badge>
              <p className="text-stone-600 mb-4">{cfg.message}</p>
              <p className="text-sm text-stone-400">Services: {existingApp.services?.join(", ")}</p>
              <div className="flex gap-3 justify-center mt-6">
                <Button variant="outline" className="rounded-full" onClick={() => navigate("/profile")} data-testid="back-to-profile-btn">
                  <ArrowLeft className="w-4 h-4 mr-2" /> Back to Profile
                </Button>
                {existingApp.status === "reapply_allowed" && (
                  <Button className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white" onClick={handleReapply} data-testid="reapply-btn">
                    Reapply
                  </Button>
                )}
              </div>
            </div>
          </div>
        </section>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50" data-testid="professional-verification-page">
      <Navbar />

      <section className="relative pt-32 pb-12">
        <div className="absolute inset-0 bg-gradient-to-r from-emerald-950 via-emerald-900 to-emerald-950"></div>
        <div className="section-container relative z-10">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-lime-400 rounded-2xl flex items-center justify-center">
                <Shield className="w-7 h-7 text-emerald-950" />
              </div>
              <div>
                <h1 className="text-3xl md:text-4xl font-bold text-white">Become a Professional</h1>
                <p className="text-emerald-300 mt-1">Complete verification to offer services on Stack</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Steps indicator */}
      <div className="max-w-2xl mx-auto px-4 pt-8">
        <div className="flex items-center justify-center gap-3 mb-8">
          {[1, 2, 3].map(s => (
            <div key={s} className="flex items-center gap-2">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${step >= s ? "bg-emerald-950 text-white" : "bg-stone-200 text-stone-500"}`} data-testid={`step-indicator-${s}`}>
                {step > s ? <CheckCircle2 className="w-4 h-4" /> : s}
              </div>
              <span className={`text-sm hidden sm:block ${step >= s ? "text-emerald-950 font-medium" : "text-stone-400"}`}>
                {s === 1 ? "Services" : s === 2 ? "Documents" : "Review"}
              </span>
              {s < 3 && <div className={`w-12 h-0.5 ${step > s ? "bg-emerald-500" : "bg-stone-200"}`} />}
            </div>
          ))}
        </div>
      </div>

      <section className="pb-12 px-4">
        <div className="max-w-2xl mx-auto">
          {/* Step 1: Select Services */}
          {step === 1 && (
            <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="bg-white rounded-2xl p-6 md:p-8 border border-stone-100" data-testid="step-1-services">
              <h2 className="text-xl font-bold text-emerald-950 mb-2">What services will you offer?</h2>
              <p className="text-stone-500 mb-6 text-sm">Select all that apply. Required documents will be determined by your selection.</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {SERVICE_OPTIONS.map(svc => (
                  <button
                    key={svc.value}
                    onClick={() => toggleService(svc.value)}
                    className={`flex items-center gap-3 p-4 rounded-xl border-2 text-left transition-all ${selectedServices.includes(svc.value) ? "border-emerald-500 bg-emerald-50" : "border-stone-200 hover:border-stone-300"}`}
                    data-testid={`service-${svc.value}`}
                  >
                    <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${selectedServices.includes(svc.value) ? "bg-emerald-500 border-emerald-500" : "border-stone-300"}`}>
                      {selectedServices.includes(svc.value) && <CheckCircle2 className="w-3 h-3 text-white" />}
                    </div>
                    <span className="font-medium text-sm">{svc.label}</span>
                  </button>
                ))}
              </div>

              {/* Vehicle Type - shown when Cargo is selected */}
              {selectedServices.includes("cargo") && (
                <div className="mt-6" data-testid="vehicle-type-section">
                  <h3 className="font-semibold text-emerald-950 mb-2 flex items-center gap-2">
                    <Truck className="w-4 h-4" /> Select Your Vehicle Type
                  </h3>
                  <p className="text-stone-500 text-xs mb-3">Choose the size of vehicle you'll use for cargo deliveries.</p>
                  <div className="grid grid-cols-3 gap-3">
                    {[
                      { value: "small", label: "Pickup", desc: "Pickup Truck", capacity: "Up to 1,000 lbs",
                        icon: <svg viewBox="0 0 64 32" className="w-12 h-6 mx-auto mb-2"><path d="M8 24h4a4 4 0 008 0h12a4 4 0 008 0h8v-6l-6-8H30l-2-4H8a4 4 0 00-4 4v10a4 4 0 004 4z" fill="none" stroke="currentColor" strokeWidth="2" strokeLinejoin="round"/><circle cx="16" cy="24" r="3" fill="currentColor"/><circle cx="44" cy="24" r="3" fill="currentColor"/><line x1="30" y1="10" x2="30" y2="18" stroke="currentColor" strokeWidth="1.5"/></svg>
                      },
                      { value: "medium", label: "Cargo Van", desc: "Enclosed Van", capacity: "Up to 3,000 lbs",
                        icon: <svg viewBox="0 0 64 32" className="w-12 h-6 mx-auto mb-2"><path d="M6 24h4a4 4 0 008 0h20a4 4 0 008 0h6V8a4 4 0 00-4-4H10a4 4 0 00-4 4v16z" fill="none" stroke="currentColor" strokeWidth="2" strokeLinejoin="round"/><circle cx="14" cy="24" r="3" fill="currentColor"/><circle cx="42" cy="24" r="3" fill="currentColor"/><rect x="38" y="8" width="10" height="8" rx="1" fill="none" stroke="currentColor" strokeWidth="1.5"/></svg>
                      },
                      { value: "large", label: "Truck", desc: "Box Truck / Flatbed", capacity: "Up to 10,000 lbs",
                        icon: <svg viewBox="0 0 64 32" className="w-14 h-7 mx-auto mb-2"><path d="M4 24h2a4 4 0 008 0h6V6a2 2 0 012-2h20a2 2 0 012 2v18h2a4 4 0 008 0h4v-8l-6-10h-8V6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinejoin="round"/><circle cx="10" cy="24" r="3" fill="currentColor"/><circle cx="50" cy="24" r="3" fill="currentColor"/><rect x="4" y="6" width="28" height="18" rx="2" fill="none" stroke="currentColor" strokeWidth="1.5"/></svg>
                      },
                    ].map((v) => (
                      <button
                        key={v.value}
                        type="button"
                        onClick={() => setVehicleType(v.value)}
                        className={`p-4 rounded-xl border-2 text-center transition-all ${vehicleType === v.value ? "border-emerald-500 bg-emerald-50" : "border-stone-200 hover:border-stone-300"}`}
                        data-testid={`vehicle-${v.value}`}
                      >
                        <div className={vehicleType === v.value ? "text-emerald-600" : "text-stone-400"}>
                          {v.icon}
                        </div>
                        <div className="font-semibold text-sm text-emerald-950">{v.label}</div>
                        <div className="text-xs text-stone-500">{v.desc}</div>
                        <div className="text-[10px] text-stone-400 mt-1">{v.capacity}</div>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Roadside Sub-Services - shown when Roadside Assistance is selected */}
              {selectedServices.includes("roadside") && (
                <div className="mt-6" data-testid="roadside-services-section">
                  <h3 className="font-semibold text-emerald-950 mb-2 flex items-center gap-2">
                    <Wrench className="w-4 h-4" /> Select Your Roadside Services
                  </h3>
                  <p className="text-stone-500 text-xs mb-3">Choose which roadside services you can provide.</p>
                  <div className="space-y-2">
                    {[
                      { value: "battery_boost", label: "Battery Boost", desc: "Get a jump-start for a dead battery" },
                      { value: "flat_tire", label: "Flat Tire Change", desc: "Have your spare tire installed" },
                      { value: "fuel_delivery", label: "Fuel Delivery", desc: "Receive fuel if your tank is empty" },
                      { value: "lockout", label: "Lockout Service", desc: "Get help unlocking your vehicle" },
                      { value: "winching", label: "Winching / Extrication", desc: "Pull your vehicle out of snow, mud, or a ditch" },
                    ].map((svc) => (
                      <button
                        key={svc.value}
                        type="button"
                        onClick={() => setRoadsideServices(prev => prev.includes(svc.value) ? prev.filter(s => s !== svc.value) : [...prev, svc.value])}
                        className={`flex items-center gap-3 p-4 rounded-xl border-2 text-left transition-all w-full ${roadsideServices.includes(svc.value) ? "border-emerald-500 bg-emerald-50" : "border-stone-200 hover:border-stone-300"}`}
                        data-testid={`roadside-svc-${svc.value}`}
                      >
                        <div className={`w-5 h-5 rounded border-2 flex items-center justify-center shrink-0 ${roadsideServices.includes(svc.value) ? "bg-emerald-500 border-emerald-500" : "border-stone-300"}`}>
                          {roadsideServices.includes(svc.value) && <CheckCircle2 className="w-3 h-3 text-white" />}
                        </div>
                        <div>
                          <span className="font-medium text-sm text-emerald-950">{svc.label}</span>
                          <p className="text-xs text-stone-500">{svc.desc}</p>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Laborer Job Types - shown when General Labor is selected */}
              {selectedServices.includes("laborer") && (
                <div className="mt-6" data-testid="laborer-jobs-section">
                  <h3 className="font-semibold text-emerald-950 mb-2 flex items-center gap-2">
                    <Wrench className="w-4 h-4" /> Select Your Job Types
                  </h3>
                  <p className="text-stone-500 text-xs mb-3">Choose which labor services you can provide.</p>
                  <div className="space-y-2">
                    {[
                      { value: "loading", label: "Loading / Unloading", desc: "Moving items in/out of vehicles" },
                      { value: "packing", label: "Packing / Unpacking", desc: "Organizing and boxing items" },
                      { value: "assembly", label: "Furniture Assembly", desc: "IKEA, desks, beds, etc." },
                      { value: "general_labor", label: "General Labor", desc: "Yard work, lifting, misc tasks" },
                    ].map((job) => (
                      <button
                        key={job.value}
                        type="button"
                        onClick={() => setLaborerJobTypes(prev => prev.includes(job.value) ? prev.filter(j => j !== job.value) : [...prev, job.value])}
                        className={`flex items-center gap-3 p-4 rounded-xl border-2 text-left transition-all w-full ${laborerJobTypes.includes(job.value) ? "border-emerald-500 bg-emerald-50" : "border-stone-200 hover:border-stone-300"}`}
                        data-testid={`laborer-job-${job.value}`}
                      >
                        <div className={`w-5 h-5 rounded border-2 flex items-center justify-center shrink-0 ${laborerJobTypes.includes(job.value) ? "bg-emerald-500 border-emerald-500" : "border-stone-300"}`}>
                          {laborerJobTypes.includes(job.value) && <CheckCircle2 className="w-3 h-3 text-white" />}
                        </div>
                        <div>
                          <span className="font-medium text-sm text-emerald-950">{job.label}</span>
                          <p className="text-xs text-stone-500">{job.desc}</p>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Nanny Experience - shown when Nanny is selected */}
              {selectedServices.includes("nanny") && (
                <div className="mt-6" data-testid="nanny-experience-section">
                  <h3 className="font-semibold text-emerald-950 mb-2">Years of Childcare Experience</h3>
                  <p className="text-stone-500 text-xs mb-3">Clients will see your experience level when booking.</p>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    {[
                      { value: "0-1", label: "0–1 years" },
                      { value: "2-3", label: "2–3 years" },
                      { value: "4-6", label: "4–6 years" },
                      { value: "7+", label: "7+ years" },
                    ].map((exp) => (
                      <button
                        key={exp.value}
                        type="button"
                        onClick={() => setNannyExperience(exp.value)}
                        className={`p-3 rounded-xl border-2 text-center transition-all ${nannyExperience === exp.value ? "border-emerald-500 bg-emerald-50" : "border-stone-200 hover:border-stone-300"}`}
                        data-testid={`nanny-exp-${exp.value}`}
                      >
                        <div className={`font-semibold text-sm ${nannyExperience === exp.value ? "text-emerald-700" : "text-emerald-950"}`}>{exp.label}</div>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex justify-end mt-6">
                <Button
                  className="bg-emerald-950 hover:bg-emerald-900 text-white rounded-full px-8"
                  onClick={() => {
                    if (selectedServices.length === 0) return toast.error("Select at least one service");
                    if (selectedServices.includes("cargo") && !vehicleType) return toast.error("Please select your vehicle type for cargo");
                    if (selectedServices.includes("roadside") && roadsideServices.length === 0) return toast.error("Please select at least one roadside service");
                    if (selectedServices.includes("laborer") && laborerJobTypes.length === 0) return toast.error("Please select at least one job type for labor");
                    if (selectedServices.includes("nanny") && !nannyExperience) return toast.error("Please select your years of experience");
                    setStep(2);
                  }}
                  data-testid="step1-next-btn"
                >
                  Next <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </motion.div>
          )}

          {/* Step 2: Upload Documents */}
          {step === 2 && (
            <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="bg-white rounded-2xl p-6 md:p-8 border border-stone-100" data-testid="step-2-documents">
              <h2 className="text-xl font-bold text-emerald-950 mb-2">Upload Verification Documents</h2>
              <p className="text-stone-500 mb-6 text-sm">Upload clear photos or scans of the following documents.</p>

              {/* Selfie verification */}
              <div className="mb-6 p-4 bg-stone-50 rounded-xl border border-stone-200">
                <div className="flex items-center gap-3 mb-3">
                  <Camera className="w-5 h-5 text-emerald-600" />
                  <div>
                    <p className="font-semibold text-sm">Selfie Verification</p>
                    <p className="text-xs text-stone-500">Take a clear photo of your face</p>
                  </div>
                </div>
                {selfiePreview ? (
                  <div className="relative inline-block">
                    <img src={selfiePreview} alt="Selfie" className="w-24 h-24 rounded-xl object-cover" />
                    <button onClick={() => { setSelfieFile(null); setSelfiePreview(null); }} className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1"><X className="w-3 h-3" /></button>
                  </div>
                ) : (
                  <label className="flex items-center justify-center gap-2 p-4 border-2 border-dashed border-stone-300 rounded-xl cursor-pointer hover:border-emerald-400 transition-colors" data-testid="selfie-upload">
                    <Upload className="w-5 h-5 text-stone-400" />
                    <span className="text-sm text-stone-500">Upload Selfie</span>
                    <input type="file" accept="image/*" capture="user" className="hidden" onChange={e => handleSelfieUpload(e.target.files[0])} />
                  </label>
                )}
                {uploading.selfie && <Loader2 className="w-4 h-4 animate-spin mt-2 text-emerald-600" />}
              </div>

              {/* Required documents */}
              <div className="space-y-4">
                {requiredDocs.map(docType => {
                  const doc = DOC_LABELS[docType] || { label: docType, desc: "" };
                  const uploaded = uploadedDocs[docType];
                  return (
                    <div key={docType} className="p-4 bg-stone-50 rounded-xl border border-stone-200" data-testid={`doc-${docType}`}>
                      <div className="flex items-center gap-3 mb-2">
                        <FileText className="w-5 h-5 text-emerald-600" />
                        <div className="flex-1">
                          <p className="font-semibold text-sm">{doc.label}</p>
                          <p className="text-xs text-stone-500">{doc.desc}</p>
                        </div>
                        {uploaded && <CheckCircle2 className="w-5 h-5 text-green-500" />}
                      </div>
                      {uploaded ? (
                        <div className="flex items-center gap-2 mt-2">
                          <span className="text-xs text-green-600 bg-green-50 px-2 py-1 rounded">{uploaded.name}</span>
                          <button onClick={() => setUploadedDocs(prev => { const n = {...prev}; delete n[docType]; return n; })} className="text-xs text-red-500">Remove</button>
                        </div>
                      ) : (
                        <label className="flex items-center justify-center gap-2 p-3 mt-2 border-2 border-dashed border-stone-300 rounded-lg cursor-pointer hover:border-emerald-400 transition-colors">
                          <Upload className="w-4 h-4 text-stone-400" />
                          <span className="text-sm text-stone-500">Upload</span>
                          <input type="file" accept="image/*,.pdf" className="hidden" onChange={e => handleFileUpload(docType, e.target.files[0])} />
                        </label>
                      )}
                      {uploading[docType] && <Loader2 className="w-4 h-4 animate-spin mt-2 text-emerald-600" />}
                    </div>
                  );
                })}
              </div>

              <div className="flex justify-between mt-6">
                <Button variant="outline" className="rounded-full" onClick={() => setStep(1)} data-testid="step2-back-btn">
                  <ArrowLeft className="w-4 h-4 mr-2" /> Back
                </Button>
                <Button className="bg-emerald-950 hover:bg-emerald-900 text-white rounded-full px-8" onClick={() => setStep(3)} data-testid="step2-next-btn">
                  Next <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </motion.div>
          )}

          {/* Step 3: Review & Submit */}
          {step === 3 && (
            <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="bg-white rounded-2xl p-6 md:p-8 border border-stone-100" data-testid="step-3-review">
              <h2 className="text-xl font-bold text-emerald-950 mb-2">Review & Submit</h2>
              <p className="text-stone-500 mb-6 text-sm">Make sure everything looks correct before submitting.</p>

              <div className="space-y-4">
                <div className="p-4 bg-stone-50 rounded-xl">
                  <p className="text-xs text-stone-400 mb-1">APPLICANT</p>
                  <p className="font-medium">{user?.name}</p>
                  <p className="text-sm text-stone-500">{user?.email}</p>
                </div>

                <div className="p-4 bg-stone-50 rounded-xl">
                  <p className="text-xs text-stone-400 mb-2">SERVICES</p>
                  <div className="flex flex-wrap gap-2">
                    {selectedServices.map(s => (
                      <Badge key={s} className="bg-emerald-100 text-emerald-800">{SERVICE_OPTIONS.find(o => o.value === s)?.label || s}</Badge>
                    ))}
                  </div>
                </div>

                <div className="p-4 bg-stone-50 rounded-xl">
                  <p className="text-xs text-stone-400 mb-2">DOCUMENTS ({Object.keys(uploadedDocs).length}/{requiredDocs.length})</p>
                  {Object.entries(uploadedDocs).map(([type, data]) => (
                    <div key={type} className="flex items-center gap-2 mb-1">
                      <CheckCircle2 className="w-4 h-4 text-green-500" />
                      <span className="text-sm">{DOC_LABELS[type]?.label || type}</span>
                    </div>
                  ))}
                  {selfieFile && (
                    <div className="flex items-center gap-2 mb-1">
                      <CheckCircle2 className="w-4 h-4 text-green-500" />
                      <span className="text-sm">Selfie Verification</span>
                    </div>
                  )}
                </div>

                <div className="p-4 bg-stone-50 rounded-xl">
                  <p className="text-xs text-stone-400 mb-2">EXPERIENCE (OPTIONAL)</p>
                  <Textarea
                    value={experience}
                    onChange={e => setExperience(e.target.value)}
                    placeholder="Briefly describe your relevant experience..."
                    className="min-h-[80px]"
                    data-testid="experience-textarea"
                  />
                </div>
              </div>

              <div className="flex justify-between mt-6">
                <Button variant="outline" className="rounded-full" onClick={() => setStep(2)} data-testid="step3-back-btn">
                  <ArrowLeft className="w-4 h-4 mr-2" /> Back
                </Button>
                <Button
                  className="bg-emerald-950 hover:bg-emerald-900 text-white rounded-full px-8"
                  onClick={handleSubmit}
                  disabled={submitting}
                  data-testid="submit-application-btn"
                >
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Shield className="w-4 h-4 mr-2" />}
                  {submitting ? "Submitting..." : "Submit Application"}
                </Button>
              </div>
            </motion.div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ProfessionalVerificationPage;
